/**
 * Outbox v2 — the request a queued write will send, built ONCE by the writer (ATL-1445 stage 3,
 * P3; plan §1 "The request: one `ReplayableRequestConfig` built from the same DTO the dual-write
 * sends today").
 *
 * The build is `captureWriteRequest` (api-client, ATL-1503): the writer's ordinary typed api call,
 * run through a client whose transport answers before anything is sent. So the queued request is
 * produced by exactly the code that produces today's dual-write request — same endpoint path, same
 * date serialization, same body sanitizing, same pre-flight Zod validation — which is what the
 * request-parity tests of P9–P13 compare. Nothing here re-derives a path or a body by hand.
 *
 * `toOutboxRequest` then narrows the captured config to what the queue may persist (`OutboxRequest`):
 * a write method, a path relative to the API base, and plain JSON. It REFUSES rather than coerces:
 * a `NaN` or a `Date` that survived to here would otherwise be persisted as `null` or a string — a
 * believable wrong value (see `outboxEntry.ts`).
 */
import { captureWriteRequest, type ReplayableRequestConfig, type TypedApiClient } from '@atlas/api-client'

import {
  findNonJson,
  OUTBOX_REQUEST_METHODS,
  type JsonObject,
  type JsonValue,
  type OutboxRequest,
  type OutboxRequestMethod,
} from './outboxEntry'
import { OutboxValidationError } from './outboxStore'

function isWriteMethod(method: string): method is OutboxRequestMethod {
  return (OUTBOX_REQUEST_METHODS as readonly string[]).includes(method)
}

/** Narrow a captured request to a persistable `OutboxRequest`, or throw `OutboxValidationError`. */
export function toOutboxRequest(config: ReplayableRequestConfig): OutboxRequest {
  if (!isWriteMethod(config.method)) throw new OutboxValidationError(`request.method: ${config.method} is not a write`)
  if (typeof config.url !== 'string' || !config.url.startsWith('/') || config.url.startsWith('//')) {
    throw new OutboxValidationError('request.url: must be a path relative to the API base')
  }
  const out: OutboxRequest = { method: config.method, url: config.url }
  if (config.data !== undefined) {
    const found = findNonJson(config.data, 'request.data')
    if (found) throw new OutboxValidationError(found)
    out.data = JSON.parse(JSON.stringify(config.data)) as JsonValue
  }
  if (config.params !== undefined) {
    const found = findNonJson(config.params, 'request.params')
    if (found) throw new OutboxValidationError(found)
    out.params = JSON.parse(JSON.stringify(config.params)) as JsonObject
  }
  return out
}

/**
 * `send` issues ONE write through the given client — the same call the writer's dual-write makes,
 * e.g. `(c) => createSalesApi(c).createSale(dto)`. Resolves with the request to queue. Rejects with
 * api-client's `RequestNotCapturedError` when the live call would have been refused before sending
 * (its `reason` is that refusal), or `OutboxValidationError` when the request cannot be persisted.
 */
export async function buildOutboxRequest(send: (client: TypedApiClient) => Promise<unknown>): Promise<OutboxRequest> {
  return toOutboxRequest(await captureWriteRequest(send))
}
